import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Building2, TrendingUp, Globe, ArrowRight } from 'lucide-react';

export default function Investors() {
  const { t, language } = useLanguage();

  const assets = {
    investorHero: 'https://images.pexels.com/photos/6950015/pexels-photo-6950015.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  };

  return (
    <div className="min-h-screen bg-white pt-32">
      <section className="py-24 md:py-32 bg-gradient-to-br from-slate-900 to-slate-800 text-white" data-testid="investors-hero">
        <div className="max-w-6xl mx-auto px-6">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            {t('investors.title')}
          </h1>
          <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-3xl">
            {t('investors.subtitle')}
          </p>
        </div>
      </section>

      <section className="py-16" data-testid="business-models-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white border-2 border-slate-200 rounded-2xl p-8 hover:border-sky-600 transition-colors">
              <div className="w-16 h-16 bg-sky-100 rounded-2xl flex items-center justify-center mb-6">
                <Building2 className="w-8 h-8 text-sky-600" />
              </div>
              <h2 className="text-3xl font-semibold text-slate-900 mb-3">
                {t('investors.epc')}
              </h2>
              <p className="text-sm font-medium text-sky-600 uppercase tracking-wider mb-4">
                {t('investors.epcFull')}
              </p>
              <p className="text-base text-slate-600">
                {language === 'fr' && 'Modèle intégré couvrant l\'ingénierie, l\'approvisionnement et la construction d\'infrastructures souveraines clés en main.'}
                {language === 'en' && 'Integrated model covering engineering, procurement and construction of turnkey sovereign infrastructures.'}
                {language === 'pt' && 'Modelo integrado cobrindo engenharia, aquisição e construção de infraestruturas soberanas chave na mão.'}
                {language === 'it' && 'Modello integrato che copre ingegneria, approvvigionamento e costruzione di infrastrutture sovrane chiavi in mano.'}
              </p>
            </div>

            <div className="bg-white border-2 border-slate-200 rounded-2xl p-8 hover:border-emerald-600 transition-colors">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mb-6">
                <TrendingUp className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-3xl font-semibold text-slate-900 mb-3">
                {t('investors.bft')}
              </h2>
              <p className="text-sm font-medium text-emerald-600 uppercase tracking-wider mb-4">
                {t('investors.bftFull')}
              </p>
              <p className="text-base text-slate-600">
                {language === 'fr' && 'Construction, formation du personnel local et transfert progressif de la gestion aux équipes nationales.'}
                {language === 'en' && 'Construction, training of local staff and progressive transfer of management to national teams.'}
                {language === 'pt' && 'Construção, formação do pessoal local e transferência progressiva da gestão para as equipas nacionais.'}
                {language === 'it' && 'Costruzione, formazione del personale locale e trasferimento progressivo della gestione ai team nazionali.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50" data-testid="engagements-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-emerald-100 rounded-full mb-6">
              <p className="text-sm font-semibold text-emerald-600 uppercase tracking-wider">
                {t('investors.engagements')}
              </p>
            </div>
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-6">
              {language === 'fr' && 'Expressions d\'Intérêt Signées'}
              {language === 'en' && 'Signed Expressions of Interest'}
              {language === 'pt' && 'Expressões de Interesse Assinadas'}
              {language === 'it' && 'Espressioni di Interesse Firmate'}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {language === 'fr' && [
              { country: 'Guinée-Bissau', sectors: 'Santé, Agriculture' },
              { country: 'Nigéria', regions: 'Abuja, Delta', sectors: 'Santé, Éducation' },
              { country: 'Cameroun', regions: 'Littoral, Est', sectors: 'Santé, Agriculture, Éducation (MINEDUB)' },
            ].map((engagement, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6">
                <Globe className="w-10 h-10 text-sky-600 mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {engagement.country}
                </h3>
                {engagement.regions && (
                  <p className="text-sm text-slate-500 mb-2">
                    {engagement.regions}
                  </p>
                )}
                <p className="text-sm text-slate-600">
                  {engagement.sectors}
                </p>
              </div>
            ))}
            {language === 'en' && [
              { country: 'Guinea-Bissau', sectors: 'Health, Agriculture' },
              { country: 'Nigeria', regions: 'Abuja, Delta', sectors: 'Health, Education' },
              { country: 'Cameroon', regions: 'Littoral, East', sectors: 'Health, Agriculture, Education (MINEDUB)' },
            ].map((engagement, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6">
                <Globe className="w-10 h-10 text-sky-600 mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {engagement.country}
                </h3>
                {engagement.regions && (
                  <p className="text-sm text-slate-500 mb-2">
                    {engagement.regions}
                  </p>
                )}
                <p className="text-sm text-slate-600">
                  {engagement.sectors}
                </p>
              </div>
            ))}
            {language === 'pt' && [
              { country: 'Guiné-Bissau', sectors: 'Saúde, Agricultura' },
              { country: 'Nigéria', regions: 'Abuja, Delta', sectors: 'Saúde, Educação' },
              { country: 'Camarões', regions: 'Litoral, Este', sectors: 'Saúde, Agricultura, Educação (MINEDUB)' },
            ].map((engagement, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6">
                <Globe className="w-10 h-10 text-sky-600 mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {engagement.country}
                </h3>
                {engagement.regions && (
                  <p className="text-sm text-slate-500 mb-2">
                    {engagement.regions}
                  </p>
                )}
                <p className="text-sm text-slate-600">
                  {engagement.sectors}
                </p>
              </div>
            ))}
            {language === 'it' && [
              { country: 'Guinea-Bissau', sectors: 'Sanità, Agricoltura' },
              { country: 'Nigeria', regions: 'Abuja, Delta', sectors: 'Sanità, Istruzione' },
              { country: 'Camerun', regions: 'Litorale, Est', sectors: 'Sanità, Agricoltura, Istruzione (MINEDUB)' },
            ].map((engagement, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6">
                <Globe className="w-10 h-10 text-sky-600 mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {engagement.country}
                </h3>
                {engagement.regions && (
                  <p className="text-sm text-slate-500 mb-2">
                    {engagement.regions}
                  </p>
                )}
                <p className="text-sm text-slate-600">
                  {engagement.sectors}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="roi-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-12 text-white">
            <div className="max-w-3xl">
              <h2 className="text-3xl md:text-4xl font-semibold mb-6">
                ROI Social & Financier
              </h2>
              <p className="text-base md:text-lg text-slate-300 leading-relaxed mb-8">
                {language === 'fr' && 'LENODA structure des actifs souverains dé-risqués générant un double impact : rentabilité financière pour les investisseurs et transformation socio-économique pour les populations.'}
                {language === 'en' && 'LENODA structures de-risked sovereign assets generating a dual impact: financial profitability for investors and socio-economic transformation for populations.'}
                {language === 'pt' && 'LENODA estrutura ativos soberanos de risco reduzido gerando um duplo impacto: rentabilidade financeira para investidores e transformação socioeconômica para as populações.'}
                {language === 'it' && 'LENODA struttura asset sovrani de-rischiati generando un doppio impatto: redditività finanziaria per gli investitori e trasformazione socio-economica per le popolazioni.'}
              </p>
              <ul className="space-y-3">
                {language === 'fr' && [
                  'Transparence fiduciaire totale (Split Payment PICSU)',
                  'Protection juridique (INPI N° DSO2026004704)',
                  'Modèles EPC/BFT éprouvés',
                  'Engagements gouvernementaux signés',
                  'Conseil scientifique international',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="mt-1 w-2 h-2 rounded-full bg-sky-400 flex-shrink-0"></div>
                    <span className="text-base">{item}</span>
                  </li>
                ))}
                {language === 'en' && [
                  'Total fiduciary transparency (Split Payment PICSU)',
                  'Legal protection (INPI N° DSO2026004704)',
                  'Proven EPC/BFT models',
                  'Signed government commitments',
                  'International scientific council',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="mt-1 w-2 h-2 rounded-full bg-sky-400 flex-shrink-0"></div>
                    <span className="text-base">{item}</span>
                  </li>
                ))}
                {language === 'pt' && [
                  'Transparência fiduciária total (Split Payment PICSU)',
                  'Proteção jurídica (INPI N° DSO2026004704)',
                  'Modelos EPC/BFT comprovados',
                  'Compromissos governamentais assinados',
                  'Conselho científico internacional',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="mt-1 w-2 h-2 rounded-full bg-sky-400 flex-shrink-0"></div>
                    <span className="text-base">{item}</span>
                  </li>
                ))}
                {language === 'it' && [
                  'Trasparenza fiduciaria totale (Split Payment PICSU)',
                  'Protezione giuridica (INPI N° DSO2026004704)',
                  'Modelli EPC/BFT comprovati',
                  'Impegni governativi firmati',
                  'Consiglio scientifico internazionale',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="mt-1 w-2 h-2 rounded-full bg-sky-400 flex-shrink-0"></div>
                    <span className="text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-sky-600 text-white" data-testid="cta-dealroom">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold mb-6">
            {t('investors.dealRoomCta')}
          </h2>
          <p className="text-base md:text-lg opacity-90 mb-10">
            {language === 'fr' && 'Due Diligence complète, projections financières validées, opportunités d\'investissement structurées.'}
            {language === 'en' && 'Complete Due Diligence, validated financial projections, structured investment opportunities.'}
            {language === 'pt' && 'Due Diligence completa, projeções financeiras validadas, oportunidades de investimento estruturadas.'}
            {language === 'it' && 'Due Diligence completa, proiezioni finanziarie validate, opportunità di investimento strutturate.'}
          </p>
          <Link 
            to="/deal-room" 
            data-testid="investors-dealroom-cta"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-sky-600 rounded-full font-medium hover:bg-slate-100 transition-colors"
          >
            {language === 'fr' && 'Demander l\'Accès'}
            {language === 'en' && 'Request Access'}
            {language === 'pt' && 'Solicitar Acesso'}
            {language === 'it' && 'Richiedi Accesso'}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
